import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './FullAbout.css';
import CircularGallery from './CircularGallery';
import ScrollIndicator from './ScrollIndicator';
import { optimizeImage } from '../utils/imageOptimizer';
import useDocumentMetadata from '../utils/useDocumentMetadata';

const FullAbout = () => {
  useDocumentMetadata(
    'About Canine Crown | Trusted Pet Nutrition Brand',
    'Learn about Canine Crown India Pvt. Ltd., a pet nutrition and care brand offering quality food and wellness products for dogs and cats.'
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const textRefs = useRef([]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What makes Canine Crown pet food different?",
      a: "Our pet food is scientifically formulated with high-quality, sustainably sourced ingredients to ensure your pet receives optimal nutrition without any fillers."
    },
    {
      q: "Is your pet food suitable for all breeds?",
      a: "Yes, our diverse range of recipes offers complete and balanced nutrition tailored to meet the needs of all breeds and life stages."
    },
    {
      q: "Do your products contain artificial additives?",
      a: "No, we never use artificial colors, flavors, or preservatives. We believe in 100% natural, wholesome goodness."
    },
    {
      q: "How do I choose the right food for my pet?",
      a: "Not sure which one to pick? Reach out to our customer support team with your pet's age and breed, and we’ll be happy to provide a personalized recommendation."
    },
    {
      q: "Where are your products manufactured?",
      a: "All Canine Crown products are proudly manufactured in state-of-the-art, certified facilities adhering to the highest quality control standards."
    }
  ];

  const scrollData = [
    {
      title: "Happy Healthy Pets",
      desc: "Better Nutrition For More Energy, Playfulness, And Everyday Happiness.",
      img: "https://caninecrown.ascentbw.com/Image/About/Abt1.webp"
    },
    {
      title: "Shiny Coats & Strong Muscles",
      desc: "A balanced diet formulated to provide everything your pet needs to look and feel great.",
      img: "https://caninecrown.ascentbw.com/Image/About/Abt2.webp"
    },
    {
      title: "Tailored For Every Stage",
      desc: "From playful puppies to wise seniors, our recipes support lifelong vitality.",
      img: "https://caninecrown.ascentbw.com/Image/About/Abt3.webp"
    },
    {
      title: "Trusted Ingredients",
      desc: "We source only the finest, scientifically-backed ingredients for optimal wellness.",
      img: "https://caninecrown.ascentbw.com/Image/About/Abt4.webp"
    }
  ];

  const instaImages = [
    { image: "https://caninecrown.ascentbw.com/Image/About/AbtGal1.webp", text: "" },
    { image: "https://caninecrown.ascentbw.com/Image/About/AbtGal2.webp", text: "" },
    { image: "https://caninecrown.ascentbw.com/Image/About/AbtGal3.webp", text: "" },
    { image: "https://caninecrown.ascentbw.com/Image/About/AbtGal4.webp", text: "" },
    { image: "https://caninecrown.ascentbw.com/Image/About/AbtGal5.webp", text: "" },
    { image: "https://caninecrown.ascentbw.com/Image/About/AbtGal6.webp", text: "" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            setActiveIndex(index);
          }
        });
      },
      { root: null, rootMargin: '-30% 0px -30% 0px', threshold: 0 }
    );

    const elements = textRefs.current;
    elements.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      elements.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);


  const heroBg = isMobile
    ? 'https://caninecrown.ascentbw.com/Image/About/AboutHeroMob.webp'
    : 'https://caninecrown.ascentbw.com/Image/About/AboutHero.webp';

  return (
    <div className="full-about-page">
      {/* Hero Section */}
      <section className="fullabout-hero">
        <div
          className="fullabout-hero-bg hero-animate-down"
          style={{ backgroundImage: `url(${optimizeImage(heroBg, { width: isMobile ? 768 : 1920 })})` }}
        ></div>
        <div className="fullabout-hero-overlay"></div>
        <div className="container">
          <div className="fullabout-hero-content hero-animate-up">
            <h1>About Canine Crown</h1>
            <p>We are committed to creating high-quality, scientifically formulated pet nutrition that supports healthier, happier lives for pets.</p>
            <Link to="/products" className="fullabout-hero-btn">Explore Products</Link>
          </div>
        </div>
        <ScrollIndicator />
      </section>

      {/* Story Section */}
      <div className="fullabout-story-wrapper">
        <section className="fullabout-story container">
          <div className="story-image-col">
            <picture>
              <source media="(max-width: 768px)" srcSet={optimizeImage("https://caninecrown.ascentbw.com/Image/About/MobAbout.webp", { width: 600, height: 800 })} />
              <img
                src={optimizeImage("https://caninecrown.ascentbw.com/Image/About/About.webp", { width: 600, height: 800 })}
                alt="Dog eating from bowl"
                className="story-main-img"
                loading="lazy"
                width="600"
                height="800"
              />
            </picture>
          </div>
          <div className="story-text-col">
            <div className="story-tag">About Canine Crown</div>
            <h2>Trusted Nutrition for<br/> Happy Pets</h2>
            <p className="story-lead">
              Canine Crown India Pvt. Ltd. is a trusted pet nutrition and care brand offering quality food, treats, and grooming products for dogs and cats. Focused on nutrition, safety, and pet wellness, we create reliable products that support healthier, happier lives for pets and the people who care for them.
            </p>
            <hr className="story-divider" />
            <div className="story-mission-vision">
              <div className="mv-item">
                <h3>Our Vision</h3>
                <p>To become a trusted Indian pet care brand known for quality nutrition, reliable products, and meaningful care for dogs and cats.</p>
              </div>
              <div className="mv-item">
                <h3>Our Mission</h3>
                <p>To provide safe, affordable, and quality pet nutrition products that support the health, happiness, and everyday well-being of pets.</p>
              </div>
              <div className="mv-item">
                <h3>Our Commitment</h3>
                <p>
                  We provide quality pet food and care products that support pet health and nutrition while helping pet parents make better choices.
                </p>
              </div>
            </div>
            <div style={{ marginTop: '10px' }}>
              <Link to="/products" className="mv-btn">Discover Nutrition</Link>
            </div>
          </div>
        </section>
      </div>

      {/* Scroll Animation Section */}
      <section className="scroll-animation-section">
        <div className="scroll-header container">
          <h2 className="scroll-title">Loved By Pets. Trusted By Pet Parents.</h2>
          <p className="scroll-subtitle">
            Real stories from pet parents who trust Canine Crown for their pets' health, energy, and happiness.
          </p>
        </div>

        <div className="container scroll-main-container">
          <div className="scroll-text-col">
            {scrollData.map((item, index) => (
              <div
                key={index}
                className={`scroll-text-block ${activeIndex === index ? 'active' : ''}`}
                data-index={index}
                ref={(el) => (textRefs.current[index] = el)}
              >
                <h3>{item.title}</h3>
                <img src={optimizeImage(item.img, { width: 400, height: 300 })} alt={item.title} className="scroll-mobile-img" loading="lazy" width="400" height="300" />
                <p>{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="scroll-img-col">
            <div className="sticky-img-wrapper">
              {scrollData.map((item, index) => (
                <img
                  key={index}
                  src={optimizeImage(item.img, { width: 600, height: 800 })}
                  alt={item.title}
                  className={`scroll-img ${activeIndex === index ? 'active' : ''}`}
                  loading="lazy"
                  width="600"
                  height="800"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div className="fullabout-faq-wrapper">
        <section className="fullabout-faq-section container">
          <div className="faq-left">
            <h2>Frequently Asked Questions</h2>
            <p>Have questions about our pet nutrition and products? We've got you covered.</p>
          </div>
          <div className="faq-right">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`faq-item ${openFaq === idx ? 'open' : ''}`}
                onClick={() => toggleFaq(idx)}
              >
                <div className="faq-question">
                  <h3>{faq.q}</h3>
                  <span className="faq-icon">
                    {openFaq === idx ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                    )}
                  </span>
                </div>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Instagram Circular Gallery Section */}
      <section className="insta-gallery-section">
        <div className="insta-header">
          <h2>Follow Us On Instagram</h2>
          <a href="https://instagram.com/Caninecrown.India" target="_blank" rel="noopener noreferrer" className="insta-handle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="url(#instaGradient)">
              <defs>
                <linearGradient id="instaGradient" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#f09433" />
                  <stop offset="25%" stopColor="#e6683c" />
                  <stop offset="50%" stopColor="#dc2743" />
                  <stop offset="75%" stopColor="#cc2366" />
                  <stop offset="100%" stopColor="#bc1888" />
                </linearGradient>
              </defs>
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
            @Caninecrown.India
          </a>
        </div>

        <div className="insta-webgl-wrapper" onClick={() => window.open('https://instagram.com/Caninecrown.India', '_blank')}>
          <CircularGallery
            items={instaImages}
            bend={3}
            textColor="#ffffff"
            borderRadius={0.05}
            scrollEase={0.05}
            scrollSpeed={2}
          />
        </div>
      </section>

    </div>
  );
};

export default FullAbout;

